import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMapPin, FiGlobe, FiArrowRight, FiInfo, FiTag } = FiIcons;

const JobDetailContent = ({ job }) => {
  const orgName = job.profile?.org_name || 'Adventure Jobs';
  const locations = job.locations || [];
  const tags = job.job_tags || job.tags || [];

  return (
    <div className="max-w-2xl mx-auto space-y-12 pb-20">
      {/* 1. Company Logo - Adjusted for wider horizontal logos */}
      <div className="flex justify-start">
        <div className="min-w-[96px] max-w-[280px] h-24 sm:h-32 rounded-3xl bg-stone-50 border border-stone-100 flex items-center justify-center overflow-hidden p-4 shadow-sm">
          {job.profile?.logo_url ? (
            <img 
              src={job.profile.logo_url} 
              alt={`${orgName} company logo`} 
              className="max-w-full max-h-full object-contain w-auto h-auto"
            />
          ) : (
            <span className="text-4xl font-black text-stone-200">{orgName[0]}</span>
          )}
        </div>
      </div>

      {/* 2. Job Category & 3. Job Type */}
      <div className="flex flex-wrap gap-2">
        <span className="bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full border border-emerald-100">
          {job.category}
        </span>
        <span className="bg-stone-50 text-stone-500 text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full border border-stone-100">
          {job.job_type}
        </span>
      </div>

      {/* 4. Job Title & 5. Company Name */}
      <div className="space-y-3">
        <h1 className="text-4xl sm:text-5xl font-black text-stone-900 leading-tight tracking-tighter">
          {job.title}
        </h1>
        <p className="text-emerald-600 font-black uppercase tracking-[0.2em] text-xs">
          {orgName}
        </p>
      </div>

      {/* 6. Location(s) */}
      <div className="space-y-4">
        <h3 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] flex items-center gap-2">
          <SafeIcon icon={FiMapPin} className="text-emerald-500" /> Location(s)
        </h3>
        <div className="flex flex-wrap gap-3">
          {locations.length > 0 ? (
            locations.map((loc, idx) => (
              <span key={idx} className="text-stone-700 font-bold text-sm bg-stone-50 px-4 py-2 rounded-xl border border-stone-100">
                {loc.name}
              </span>
            ))
          ) : (
            <span className="text-stone-500 font-medium italic">Remote / Not Specified</span>
          )}
        </div>
      </div>

      {/* 7. Job Description */}
      <div className="space-y-6 pt-6 border-t border-stone-100">
        <h3 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] flex items-center gap-2">
          <SafeIcon icon={FiInfo} className="text-emerald-500" /> Job Description
        </h3>
        <div className="prose prose-stone max-w-none prose-p:text-stone-600 prose-p:leading-relaxed prose-p:font-medium prose-p:text-base prose-headings:text-stone-900 prose-headings:font-black prose-headings:uppercase prose-headings:tracking-widest prose-headings:text-sm">
          <div dangerouslySetInnerHTML={{ __html: job.description }} />
        </div>
      </div>

      {/* 7.5 Tags (New Section) */}
      {tags.length > 0 && (
        <div className="space-y-4 pt-6 border-t border-stone-100">
          <h3 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] flex items-center gap-2">
            <SafeIcon icon={FiTag} className="text-emerald-500" /> Perks & Details
          </h3>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, idx) => (
              <span key={idx} className="bg-stone-50 text-stone-400 text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-stone-100">
                {tag.tag || tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 8. How to Apply */}
      <div className="pt-10 border-t border-stone-100">
        <div className="bg-stone-900 rounded-[40px] p-8 sm:p-12 text-white shadow-2xl shadow-stone-200">
          <h4 className="text-[10px] font-black uppercase tracking-[0.3em] mb-6 text-emerald-400">Ready for the Expedition?</h4>
          
          {job.application_instructions && (
            <p className="text-stone-300 font-medium leading-relaxed mb-8 text-sm sm:text-base">
              {job.application_instructions}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={job.application_link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center bg-emerald-500 text-white font-black px-10 py-5 rounded-2xl hover:bg-emerald-400 transition-all shadow-xl shadow-emerald-900/40 uppercase tracking-widest text-xs flex items-center justify-center gap-3 group"
            >
              Apply Now
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </a>
            
            {job.application_method === 'link' && (
              <div className="flex items-center justify-center px-4 py-2 text-stone-500 text-[9px] font-bold uppercase tracking-widest text-center">
                Redirects to company portal
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailContent;